import { Search as SearchIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import Form from '../forms/Form.js';
import FormInput from '../forms/FormInput.js';
import FormSubmit from '../forms/FormSubmit.js';
import useFormKey from '../forms/useFormKey.js';
import styles from './SearchBar.module.css';

const SearchBar = ({
  placeholder,
  onSearch,
  initialValue,
  forceLoading,
  debounceMs = 300,
  disableDebounce = false,
}) => {
  const { t } = useTranslation();
  const formKey = useFormKey();
  const [inputValue, setInputValue] = useState(initialValue || '');
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    setInputValue(initialValue || '');
  }, [initialValue]);

  const handleSubmit = (_values, done) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    onSearch({ query: inputValue });
    done();
  };

  const handleInputChange = useCallback(
    (value) => {
      setInputValue(value);

      if (disableDebounce) {
        return;
      }

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        onSearch({ query: value });
      }, debounceMs);
    },
    [onSearch, debounceMs, disableDebounce]
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <Form className={styles.form} formKey={formKey} onSubmit={handleSubmit}>
      <FormInput
        className={styles.input}
        name="query"
        placeholder={placeholder ? placeholder : t('common.search')}
        value={inputValue}
        onChange={handleInputChange}
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            if (debounceTimerRef.current) {
              clearTimeout(debounceTimerRef.current);
            }
            onSearch({ query: inputValue });
          }
        }}
      />
      <FormSubmit className={styles.submit} forceLoading={forceLoading}>
        <SearchIcon className={styles.icon} />
      </FormSubmit>
    </Form>
  );
};

SearchBar.propTypes = {
  onSearch: PropTypes.func.isRequired,
};

export default SearchBar;
